import { RefObject, useEffect, useRef } from 'react'

const FOCUSABLE = 'button:not([disabled]), a[href], [data-viewer-seek]'

type KeyEvent = { key: string; shiftKey: boolean; preventDefault: () => void }

/**
 * Keeps Tab inside a modal player. Buttons of slides that are off screen are
 * skipped: only the active slide and the controls outside the slides count.
 * So are buttons drawn hidden (a player's own controls under the custom
 * ones): they cannot take the focus, and Tab would stop on them.
 */
export const trapFocus = (event: KeyEvent, container: HTMLElement | null) => {
  if (event.key !== 'Tab' || !container) {
    return
  }
  const items = Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE)
  ).filter(el => {
    const slide = el.closest('.swiper-slide')
    return (
      (!slide || slide.classList.contains('swiper-slide-active')) &&
      el.getClientRects().length > 0 &&
      getComputedStyle(el).visibility !== 'hidden'
    )
  })
  event.preventDefault()
  if (items.length === 0) {
    return
  }
  const current = items.indexOf(document.activeElement as HTMLElement)
  const step = event.shiftKey ? -1 : 1
  const next =
    current === -1
      ? event.shiftKey
        ? items.length - 1
        : 0
      : (current + step + items.length) % items.length
  items[next].focus()
}

/**
 * A full-screen player as a modal dialog for keyboard users: the focus moves
 * into it when it opens, Escape closes it, and when it closes the focus goes
 * back to whatever opened it. Returns the onKeyDown for the container (Tab trap).
 * `active`: a dialog kept mounted while closed is one only while it is open.
 */
export const useModalDialog = (
  ref: RefObject<HTMLElement>,
  onClose: () => void,
  active = true
) => {
  const closeRef = useRef(onClose)
  closeRef.current = onClose

  useEffect(() => {
    if (!active) {
      return undefined
    }
    const opener = document.activeElement as HTMLElement | null
    ref.current?.focus()
    const onKeyDown = (e: KeyboardEvent) => {
      // an Escape that leaves fullscreen or that someone else handled is not ours
      if (
        e.key === 'Escape' &&
        !e.defaultPrevented &&
        !document.fullscreenElement
      ) {
        closeRef.current()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      // ponytail: a card recycled by the strip's virtual list is gone; the focus then stays on the page
      if (opener && opener !== document.body && opener.isConnected) {
        opener.focus({ preventScroll: true })
      }
    }
  }, [ref, active])

  return (event: KeyEvent) => trapFocus(event, ref.current)
}
